import { useState, useEffect, useCallback } from "react";

export type ViewMode = "explore" | "organizer";

const VIEW_MODE_CHANGE_EVENT = "viewModeChange";

const ATTENDEE_PATHS = ["/", "/events", "/my-tickets"];

export const syncViewModeForAuthReturn = (returnTo: string) => {
  const pathname = returnTo.split(/[?#]/, 1)[0] || "/";
  const isAttendeePath =
    ATTENDEE_PATHS.includes(pathname) ||
    (pathname.startsWith("/event/") && !pathname.endsWith("/edit"));

  if (!isAttendeePath) return;

  localStorage.setItem("viewMode", "explore");
  window.dispatchEvent(new CustomEvent(VIEW_MODE_CHANGE_EVENT, { detail: "explore" }));
};

// Normalize legacy localStorage values from removed roles back to a default.
// "promoter" was retired - it now falls back to "explore" so promoters land
// on the attendee experience and reach their dashboard via Profile > Promoter.
const normalizeSavedMode = (saved: string | null, fallback: ViewMode = "explore"): ViewMode => {
  if (saved === "explore" || saved === "organizer") {
    return saved;
  }
  return fallback;
};

export const useViewMode = () => {
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    return normalizeSavedMode(localStorage.getItem("viewMode"));
  });

  // Listen for changes from other components
  useEffect(() => {
    const handleViewModeChange = (event: CustomEvent<ViewMode>) => {
      setViewModeState(event.detail);
    };

    window.addEventListener(VIEW_MODE_CHANGE_EVENT, handleViewModeChange as EventListener);

    return () => {
      window.removeEventListener(VIEW_MODE_CHANGE_EVENT, handleViewModeChange as EventListener);
    };
  }, []);

  const setViewMode = useCallback((mode: ViewMode) => {
    const safe: ViewMode = mode === "organizer" ? "organizer" : "explore";
    localStorage.setItem("viewMode", safe);
    setViewModeState(safe);
    window.dispatchEvent(new CustomEvent(VIEW_MODE_CHANGE_EVENT, { detail: safe }));
  }, []);

  const toggleViewMode = useCallback(() => {
    const newMode: ViewMode = viewMode === "explore" ? "organizer" : "explore";
    setViewMode(newMode);
  }, [viewMode, setViewMode]);

  return { viewMode, setViewMode, toggleViewMode };
};
